import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Fade, Stack, Chip } from '@mui/material';
import { Dashboard, Receipt, Support, StarBorder } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from '@refinedev/core';
import Lottie from 'lottie-react';
import { motion } from 'framer-motion';
import successAnimation from '../../assets/animations/success-animation.json';

const SuccessPage: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Déclencher l'affichage du contenu après un délai pour l'effet
    const timer = setTimeout(() => setShowContent(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleNavigateWithDelay = (path: string) => {
    // Animation de sortie avant navigation
    setShowContent(false);
    setTimeout(() => navigate(path), 300);
  };

  return (
    <Box
      sx={{
        minHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 4,
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 6 },
            maxWidth: 900,
            textAlign: 'center',
            borderRadius: 4,
            background: 'transparent',
          }}
        >
          {/* Animation Lottie */}
          <Box sx={{ width: 120, height: 120, mx: 'auto', mb: 3 }}>
            <Lottie
              animationData={successAnimation}
              loop={false}
              style={{ width: '100%', height: '100%' }}
            />
          </Box>

          <Fade in={showContent} timeout={1000}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#3BBC5E',
                  fontSize: { xs: '2rem', sm: '3rem' },
                }}
              >
                {translate('payment.success.title')}
              </Typography>

              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6 }}
              >
                {translate('payment.success.subtitle')}
                <br />
                {translate('payment.success.message')}
              </Typography>

              {/* Badges des avantages */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 4, justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}
              >
                <Chip
                  icon={<StarBorder />}
                  label={translate('payment.success.badges.freeTrial')}
                  sx={{
                    backgroundColor: '#3BBC5E',
                    color: 'white'
                  }}
                  size="small"
                />
                <Chip
                  icon={<Support />}
                  label={translate('payment.success.badges.prioritySupport')}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={translate('payment.success.badges.cancelAnytime')}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              </Stack>

              {/* Actions */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'center' }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Dashboard />}
                  onClick={() => handleNavigateWithDelay('/')}
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 3,
                    backgroundColor: '#3BBC5E',
                    '&:hover': {
                      backgroundColor: '#2FA64D',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(59, 188, 94, 0.3)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {translate('payment.success.actions.dashboard')}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Receipt />}
                  onClick={() => handleNavigateWithDelay('/pricing')}
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 3,
                    borderWidth: 2,
                    borderColor: '#3BBC5E',
                    color: '#3BBC5E',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: '#3BBC5E',
                      backgroundColor: 'rgba(59, 188, 94, 0.08)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(59, 188, 94, 0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {translate('payment.success.actions.details')}
                </Button>
              </Stack>

              {/* Note de remerciement */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 4,
                  display: 'block',
                  fontStyle: 'italic',
                }}
              >
                {translate('payment.success.thanks')}
              </Typography>
            </Box>
          </Fade>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default SuccessPage;
